import { useEffect, useState } from "react";
import { NotesAPI, NoteEditPolicy } from "../services/notes.service";

// The Doctor Notes edit policy is configured in clinic settings and enforced
// (client-side, convenience only — the server re-checks) wherever the notes
// panel decides whether to enable a doctor's "Edit" button.

const CACHE_KEY = "kgz_note_edit_policy_cache";

// Fail-safe default: until the real policy is known, assume the stricter
// primary_doctor_only so we never over-grant edit access on a failed/slow
// fetch. The server enforces the real policy regardless of this UI gate.
const DEFAULT_POLICY: NoteEditPolicy = "primary_doctor_only";

function readCache(): NoteEditPolicy | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw === "primary_doctor_only" || raw === "any_clinic_doctor" ? raw : null;
  } catch {
    return null;
  }
}

export function cacheNoteEditPolicy(policy: NoteEditPolicy): void {
  try {
    localStorage.setItem(CACHE_KEY, policy);
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

/**
 * Read the clinic's Doctor Notes edit policy.
 * Seeds synchronously from the cache, then refreshes from the API. Falls
 * back to the cache / strict default when the network is unavailable.
 */
export function useNoteEditPolicy(): NoteEditPolicy {
  const [policy, setPolicy] = useState<NoteEditPolicy>(() => readCache() ?? DEFAULT_POLICY);

  useEffect(() => {
    let cancelled = false;
    NotesAPI.getEditPolicy()
      .then(data => {
        if (cancelled) return;
        setPolicy(data.note_edit_policy);
        cacheNoteEditPolicy(data.note_edit_policy);
      })
      .catch(() => { /* keep cached / default — fail closed to the stricter policy */ });
    return () => { cancelled = true; };
  }, []);

  return policy;
}
